import axios from "axios";
import * as tokenService from "./tokenService";

/**
 * Single-flight token refresh.
 *
 * SHARED FILE — byte-identical in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical: prx_buyerapp-ui).
 *
 * - One in-flight promise per bundle: every concurrent 401 awaits the SAME
 *   refresh (this IS the request queue the spec asks for).
 * - Web Locks (where available) serialize refreshes across tabs and across
 *   the four SPA bundles on the shared origin; the backend's rotation grace
 *   window covers browsers without it.
 * - Uses BARE axios on purpose: the app instances' interceptors (and
 *   makeApiCall's error swallowing) must never wrap the refresh call itself.
 */

// Shell uses REACT_APP_API_BASE_URL, the module apps REACT_APP_API_LOGIN_URL.
const API_BASE =
  process.env.REACT_APP_API_LOGIN_URL || process.env.REACT_APP_API_BASE_URL || "";

let inFlight: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  if (inFlight) return inFlight;
  const entryExpiresAt = tokenService.getExpiresAt();
  inFlight = runExclusive(entryExpiresAt).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runExclusive(entryExpiresAt: number | null): Promise<boolean> {
  const locks = (navigator as any)?.locks;
  if (locks?.request) {
    try {
      return await locks.request("prx-token-refresh", () => doRefresh(entryExpiresAt));
    } catch {
      // Lock machinery itself failed — refresh without it; the backend grace
      // window absorbs a cross-tab race.
      return doRefresh(entryExpiresAt);
    }
  }
  return doRefresh(entryExpiresAt);
}

async function doRefresh(entryExpiresAt: number | null): Promise<boolean> {
  // While we waited on the lock another tab may have already refreshed.
  // Only skip when the stored expiry MOVED since we entered — a merely
  // "not yet expired" clock must not suppress a refresh the server demanded
  // with a 401.
  const currentExpiresAt = tokenService.getExpiresAt();
  if (
    currentExpiresAt != null &&
    currentExpiresAt !== entryExpiresAt &&
    !tokenService.isAccessTokenExpired(0)
  ) {
    return true;
  }

  try {
    const authHeader = tokenService.getAccessTokenRaw();
    const { data } = await axios.post(
      `${API_BASE}/auth/refresh`,
      {},
      {
        withCredentials: true,
        headers: authHeader ? { Authorization: authHeader } : {},
      }
    );
    if (data?.status && data?.token?.access_token) {
      tokenService.setTokens(data.token, data.accessTokenExpiresIn, data.session);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
